import { useGoogleLogin } from '@react-oauth/google';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import PageLoading from '@/components/Common/PageLoading';

import { Flex, Space } from '../../components/base';
import SynclyLogo from '../../components/Common/SynclyLogo';
import Typo from '../../components/Typography/Typo';
import useColor from '../../hooks/domain/useColor';
import useCrossWindowState from '../../hooks/domain/useCrossWindowState';
import useSidebar from '../../hooks/domain/useSidebar';
import useCommonStore from '../../stores/pure/CommonStore';
import useUserStore from '../../stores/UserStore';
import useWorkspaceStore from '../../stores/WorkspaceStore';
import { GORGIAS_ACCOUNT_SEARCH_PARAM } from '../Setting/Integrations/integrationRedirection/gorgias-redirect';
import type { MicrosoftResponse } from '../Setting/Integrations/integrationRedirection/microsoft';
import { OktaLoginButton, useAuth0LoginFlow } from './auth0-login-flow';
import { useOktaIdpInitiation } from './use-okta-idp-initiation';

const LoginPage = () => {
  const color = useColor();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth0Organization = searchParams.get('auth0_organization');
  const returnTo = searchParams.get('from');
  const initWholeStore = useCommonStore((state) => state.initWholeStore);
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const { verfiyUser, user, inviteWorkspace } = useUserStore((state) => ({
    verfiyUser: state.verfiyUser,
    user: state.user,
    inviteWorkspace: state.inviteWorkspace,
  }));

  const [verified, setVerified] = useState(false);
  const [invited, setInvited] = useState(false);
  const oktaLoginIntent = useOktaIdpInitiation(auth0Organization, returnTo);
  const oktaLogin = useAuth0LoginFlow({
    intent: oktaLoginIntent,
    onVerified: setVerified,
  });

  const [popup, setPopup] = useState<Window | null>(null);

  const [state, _, removeKey] = useCrossWindowState<MicrosoftResponse | null>(
    'microsoft-redirect',
    null,
  );

  const handlePopup = () => {
    const popupUrl =
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      'response_type=code&' +
      `client_id=${import.meta.env.VITE_PUBLIC_MICROSOFT_AUTH_CLIENT_ID}&` +
      `redirect_uri=${window.location.origin}/microsoft&` +
      'scope=openid profile email https://graph.microsoft.com/User.Read';

    const newPopup = window.open(
      popupUrl,
      'MicrosoftLoginPopup',
      'width=600,height=600',
    );
    setPopup(newPopup);
  };

  const onSuccessHandler = async (credentialResponse: {
    access_token: string;
  }) => {
    const verified = await verfiyUser(
      credentialResponse.access_token,
      'google',
    );
    setVerified(verified);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onSuccessHandler,
  });

  const microsoftLogin = () => {
    handlePopup();
  };

  useEffect(() => {
    if (state) {
      const { code } = state;
      if (code) {
        const callbackHandler = async () => {
          const verified = await verfiyUser(code, 'microsoft');
          setVerified(verified);
          removeKey();
          popup && popup.close();
        };
        callbackHandler();
      }
    }
  }, [state]);

  const checkVerified = useCallback(async () => {
    const inviteFunc = async (code: string) => {
      const data = await inviteWorkspace(code);
      if (data) {
        if (data.alias) {
          navigate(`/workspace/${data.alias}`, {
            replace: true,
          });
        } else if (data.isDuplicated) {
          //추후 중복처리로 alias 찾아 리턴하게끔 할지 고민..
        }
      } else {
        alert(
          'This invitation link is no longer valid. Please contact your administrator.',
        );
      }
    };

    const gorgiasRedirectFunc = async (gorgias_account: string) => {
      navigate(`/gorgias-redirect?account=${gorgias_account}`);
    };

    const gorgias_account = searchParams.get(GORGIAS_ACCOUNT_SEARCH_PARAM);

    if (searchParams.get('invite')) {
      if (verified && user && !invited) {
        setInvited(true);
        inviteFunc(searchParams.get('invite') as string);
      }
    } else if (gorgias_account) {
      if (verified && user) {
        gorgiasRedirectFunc(gorgias_account);
      }
    } else {
      if (verified && user) {
        if (user.hasWorkspace) {
          if (searchParams.get('from')) {
            navigate(decodeURIComponent(searchParams.get('from') as string), {
              replace: true,
            });
          } else {
            navigate('/', {
              replace: true,
            });
          }
        } else {
          navigate('/create-workspace', {
            replace: true,
          });
        }
      }
    }
  }, [verified, user, searchParams, navigate, inviteWorkspace, invited]);

  useEffect(() => {
    checkVerified();
  }, [checkVerified]);

  useSidebar(false);

  useEffect(() => {
    if (!currWorkspace) return;
    initWholeStore();
  }, [initWholeStore, currWorkspace]);

  if (oktaLogin.status === 'pending') {
    return (
      <PageLoading
        style={{ position: 'relative' }}
        wrapperStyle={{ backgroundColor: 'transparent' }}
      />
    );
  }

  return (
    <Style j="center" a="center" d="column" style={{ height: '100vh' }}>
            <Space h="120px" />
            <Flex className="login-form-wrapper" a="center" d="column">
              <SynclyLogo direction="column" size={64} center />
              <Space h="8px" />
              <Typo type="label1" color={color.gray600}>
                Sign in to your workspace
              </Typo>
              <Space h="32px" />

              <Flex d="column" style={{ width: '300px' }}>
                {oktaLogin.errorMessage ? (
                  <>
                    <Typo type="body2" color={color.red600}>
                      {oktaLogin.errorMessage}
                    </Typo>
                    <Space h="12px" />
                  </>
                ) : null}
                <Button
                  sizeType="l"
                  onClick={() => googleLogin()}
                  variant="outlined"
                  style={{
                    width: '100%',
                    color: color.gray700,
                  }}
                >
                  <img
                    src="/assets/logo-google.svg"
                    alt="google login"
                    width="22px"
                    height="22px"
                  />
                  Continue with Google
                </Button>

                <Space h="12px" />
                <Button
                  sizeType="l"
                  variant="outlined"
                  style={{
                    width: '100%',
                    color: color.gray700,
                  }}
                  onClick={() => microsoftLogin()}
                >
                  <img
                    src="/assets/logo-microsoft.svg"
                    alt="google login"
                    width="14px"
                    height="14px"
                  />
                  <Space w="1px" />
                  Continue with Microsoft
                </Button>
                <OktaLoginButton onClick={oktaLogin.login} />
              </Flex>
            </Flex>
            <Space h="180px" />
    </Style>
  );
};

const Style = styled(Flex)`
  width: 100%;
  .login-form-wrapper {
    width: 100%;
    max-width: 400px;
  }
  .btn-login {
    cursor: pointer;
    border: 1px solid ${(props) => props.theme.colors.gray500};
    height: 42px;
    width: 100%;
    border-radius: 4px;
  }
`;

export default LoginPage;
